import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import styled from 'styled-components'
import * as Icon from 'react-feather'
import _ from 'lodash'
import uploadFile from '~/api/uploadFile'

import {
  Nav,
  NavLink,
  NavItem,
  TabContent,
  TabPane,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
} from 'reactstrap'
import EditableRow from '~/components/EditableRow'
import DropzoneImageInput from './DropzoneImageInput'
import { LoadableCard, Show } from '~/components/commons'
import useAxios from 'axios-hooks'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { CallbackAlert } from '~/components/CallbackAlert'
import Avatar from '~/components/Avatar'
import { toast } from 'react-toastify'
import { EditedFields } from '..'

const ZoomImageModal = ({
  image = {},
  isOpen = true,
  toggle = () => {},
  onExcludeImage = () => {},
  excludingImage,
}) => {
  const getTitleImage = image => {
    const category = image?.locationAssociation?.category
    if (category) {
      return `${_.capitalize(category)} image`
    }

    return 'Location image'
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" wrapClassName="d-flex">
      <ModalHeaderWithButtons tag="div" className="d-flex">
        <Show condition={image?.attribution}>
          <div className="d-flex align-items-center">
            <Avatar
              className="m-0"
              bgColor="#EBE9FD"
              img={image?.attribution?.profilePhotoUrl}
            />
            <div className="ml-1">
              <p className="mb-50">
                <FormattedMessage id="Customer image" />
              </p>
              <p className="mb-0">
                <FormattedMessage id="Sent by image" />:
                <b>{image?.attribution?.profileName}</b>
              </p>
            </div>
          </div>
        </Show>
        <Show condition={!image?.attribution}>
          <p>{getTitleImage(image)}</p>
        </Show>
        <div className="d-flex justify-content-end">
          <Show condition={!image?.attribution}>
            <Button
              color="danger"
              className="d-flex align-items-center p-50 align-self-center mr-1"
              onClick={() => onExcludeImage(image)}
              disabled={excludingImage}
            >
              <Icon.Trash2 size={14} className="mr-50" />
              <FormattedMessage id="Remove image" />
            </Button>
          </Show>
          <Show condition={image?.attribution?.takedownUrl}>
            <ReportButton
              color="danger"
              className="d-flex align-items-center align-self-center mr-1"
              href={image?.attribution?.takedownUrl}
              target="_blank"
            >
              <Icon.AlertCircle size={14} className="mr-50" />
              <FormattedMessage id="Report image" />
            </ReportButton>
          </Show>
          <Button className="p-25" color="white" onClick={() => toggle()}>
            <Icon.X className="danger" />
          </Button>
        </div>
      </ModalHeaderWithButtons>
      <ModalBody className="relative p-0">
        <LoadableCard loading={excludingImage} className="shadow-none p-0">
          <Show condition={image?.insights?.viewCount}>
            <ViewsCount>
              <Icon.Eye className="mr-50" size={16} />
              <p className="mb-0">
                {image?.insights?.viewCount} <FormattedMessage id="Views" />
              </p>
            </ViewsCount>
          </Show>
          {image?.googleUrl && <ZoomImage src={image.googleUrl} />}
        </LoadableCard>
      </ModalBody>
    </Modal>
  )
}

const AdditionalImages = ({
  id = 'ADDITIONAL',
  onSwitchChange = () => {},
  onSubmit = () => {},
  onZoomImage = () => {},
  onExcludeImage = () => {},
  excludingImage,
  editedFields,
  images = [],
  sendButtonLabel = 'Send',
  hintText = 'Example hint text',
  labelListPreview = 'List images',
  successMessage = 'Images successfully updated!',
  hideDropzone = false,
}) => {
  const [imageFiles, setImageFiles] = useState()
  const [submitting, setSubmitting] = useState(false)
  const [successfullyUpdated, setSuccessfullyUpdated] = useState(false)
  const additionalDropzoneRef = useRef()

  async function handleSubmit() {
    setSubmitting(true)
    onSubmit(imageFiles, id)
      .then(() => {
        setSuccessfullyUpdated(true)
        if (additionalDropzoneRef?.current?.cleanPreview)
          additionalDropzoneRef.current.cleanPreview()
        onSwitchChange(id)
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <div className="row mx-0">
      <Show condition={!hideDropzone}>
        <div className="col-md-12 d-flex justify-content-between">
          <p>
            <FormattedMessage id="Send images" />:
          </p>
          <EditableRow
            switchId={id}
            onChange={switchId => onSwitchChange(switchId)}
            isChecked={editedFields?.[id]}
          />
        </div>
        <div className="col-md-12">
          <DropzoneImageInput
            ref={additionalDropzoneRef}
            onChange={setImageFiles}
            disabled={!editedFields?.[id]}
            multiple
          />
          <p>
            <em>{hintText}</em>
          </p>

          <Show condition={!successfullyUpdated}>
            <Button
              color="primary"
              disabled={
                !editedFields?.[id] || !imageFiles?.length || submitting
              }
              onClick={() => handleSubmit()}
            >
              <Show condition={!submitting}>{sendButtonLabel}</Show>
              <Show condition={submitting}>
                <FormattedMessage id="Sending" />
                ...
                <Spinner size="sm" />
              </Show>
            </Button>
          </Show>
          <Show condition={successfullyUpdated}>
            <p className="mb-0 success">
              <Icon.CheckCircle size={16} className="mr-50" />
              {successMessage}
            </p>
          </Show>
        </div>
      </Show>
      <div className="col-md-12 flex-wrap mt-1">
        <Show condition={images?.length}>
          <p>{labelListPreview}</p>
        </Show>
        <Show condition={!images?.length}>
          <p>
            <FormattedMessage id="No images added yet" />.
          </p>
        </Show>
        <AdditionalListPreview>
          <Show condition={images?.length}>
            {(images || []).map((image, i) => (
              <AdditionalImageCard key={`additional-${id}-${i}`}>
                <AdditionalImageCard.MaximizeButton
                  onClick={() => onZoomImage(image)}
                >
                  <Icon.Maximize />
                </AdditionalImageCard.MaximizeButton>
                <Show condition={image?.attribution?.takedownUrl}>
                  <AdditionalImageCard.ReportButton
                    href={image?.attribution?.takedownUrl}
                    target="_blank"
                  >
                    <Icon.AlertCircle size={14} />
                  </AdditionalImageCard.ReportButton>
                </Show>
                <Show condition={!image?.attribution}>
                  <AdditionalImageCard.ExcludeButton
                    color="danger"
                    onClick={() => onExcludeImage(image)}
                    disabled={excludingImage}
                  >
                    <Icon.Trash2 size={14} />
                  </AdditionalImageCard.ExcludeButton>
                </Show>
                <img src={image.googleUrl} alt={`${id} - ${i + 1}`} />
              </AdditionalImageCard>
            ))}
          </Show>
        </AdditionalListPreview>
      </div>
    </div>
  )
}

const EditImagesForm = () => {
  const {
    editedFields: { images: editedFields },
    setEditedFields,
  } = useContext(EditedFields)
  const [activeTab, setActiveTab] = useState('1')
  const [locationImages, setLocationImages] = useState({})
  const [logoImage, setLogoImage] = useState({
    files: null,
    submitting: false,
    updateSuccessfully: false,
  })
  const [coverImage, setCoverImage] = useState({
    files: null,
    submitting: false,
    updateSuccessfully: false,
  })
  const [zoomImage, setZoomImage] = useState()
  const [deleteImage, setDeleteImage] = useState()
  const [confirmDeleteImage, setConfirmDeleteImage] = useState(false)
  const [excludingImage, setExcludingImage] = useState(false)

  const projectId = useSelector(state => state.user?.selectedProject?.id)
  const { locationId } = useParams()
  const logoDropzoneRef = useRef()
  const coverDropzoneRef = useRef()
  const [, fetchLocationImages] = useAxios(
    `/projects/${projectId}/locations/${locationId}/media`,
    {
      manual: true,
    }
  )

  const [, updateLocationImages] = useAxios(
    `/projects/${projectId}/locations/${locationId}/images`,
    {
      manual: true,
    }
  )

  const [, removeLocationImage] = useAxios(
    `/projects/${projectId}/locations/${locationId}/images`,
    {
      manual: true,
    }
  )

  const fillLocationImages = useCallback(() => {
    fetchLocationImages()
      .then(({ data }) => {
        setLocationImages(data)
      })
      .catch(error => {
        console.error(error)
      })
  }, [fetchLocationImages])

  const handleDeleteImage = useCallback(
    async image => {
      setExcludingImage(true)
      try {
        await removeLocationImage({
          method: 'DELETE',
          data: {
            mediaId: image.name,
          },
        })
        const type = image?.locationAssociation?.category
        setLocationImages(prev => ({
          ...(prev || {}),
          [type]: prev[type].filter(img => img.name !== image.name),
        }))
        setZoomImage(null)
        setExcludingImage(false)
      } catch (error) {
        console.error(error)
        toast.error('Could delete this location image, try again later.')
      } finally {
        setExcludingImage(false)
      }
    },
    [removeLocationImage]
  )

  const addPreviewNewImages = (images = []) => {
    const singleImageCategories = ['LOGO', 'PROFILE']
    images.forEach(image => {
      const category = image?.locationAssociation?.category
      const isSingle = singleImageCategories.includes(category)
      if (category) {
        setLocationImages(prev => ({
          ...(prev || {}),
          [category]:
            prev?.[category] && isSingle
              ? [image]
              : [...(prev?.[category] || []), image],
        }))
      }
    })
  }

  const handleUpdateCoverOrLogoImage = ({
    type,
    submitting = false,
    updateSuccessfully = false,
  }) => {
    if (type === 'COVER') {
      setCoverImage(prev => ({
        ...(prev || {}),
        submitting,
        updateSuccessfully,
      }))
    } else {
      setLogoImage(prev => ({
        ...(prev || {}),
        submitting,
        updateSuccessfully,
      }))
    }
  }

  const handleSubmitImages = useCallback(
    async (files = [], id) => {
      const uploadedImages = {}
      const filesArray = _.castArray(files)
      const promisePicsUpload = filesArray.map(
        file =>
          new Promise((resolve, reject) => {
            uploadFile([file])
              .then(({ data: picture }) => {
                if (!uploadedImages[id]) uploadedImages[id] = []
                uploadedImages[id].push(picture)
                resolve(picture)
              })
              .catch(error => {
                toast.error(
                  `We were unable to upload your ${id} image. try again.`
                )
                reject(error)
              })
          })
      )

      try {
        if (['COVER', 'PROFILE'].includes(id)) {
          handleUpdateCoverOrLogoImage({
            type: id,
            submitting: true,
          })
        }
        const pictures = await Promise.all(promisePicsUpload)
        const images = {
          [id]: pictures,
        }

        const { data } = await updateLocationImages({
          method: 'POST',
          data: {
            images,
          },
        })

        addPreviewNewImages(data.medias)
        if (['COVER', 'PROFILE'].includes(id)) {
          handleUpdateCoverOrLogoImage({
            type: id,
            updateSuccessfully: true,
            submitting: false,
          })

          if (id === 'COVER') {
            coverDropzoneRef.current.cleanPreview()
          } else {
            logoDropzoneRef.current.cleanPreview()
          }
        }
        handleSwitchChange(id)
        toast.success(data.message)
      } catch (error) {
        console.error(error)
        const message =
          error?.response?.data?.message || `Could update your location image.`
        toast.error(message)
      }
    },
    // eslint-disable-next-line
    [updateLocationImages]
  )

  const handleSwitchChange = switchId => {
    setEditedFields(prevState => ({
      ...prevState,
      images: {
        ...prevState.images,
        [switchId]: !editedFields[switchId],
      },
    }))
  }

  useEffect(() => {
    fillLocationImages()
  }, [fillLocationImages])

  return (
    <>
      <Nav tabs className="nav-top mt-1 box-shadow-0">
        <NavItem>
          <NavLinkPane
            className={classNames({
              active: activeTab === '1',
            })}
            onClick={() => setActiveTab('1')}
          >
            <FormattedMessage id="Logo and Cover" />
          </NavLinkPane>
        </NavItem>
        <NavItem>
          <NavLinkPane
            className={classNames({
              active: activeTab === '2',
            })}
            onClick={() => setActiveTab('2')}
          >
            <FormattedMessage id="Interior" />
          </NavLinkPane>
        </NavItem>
        <NavItem>
          <NavLinkPane
            className={classNames({
              active: activeTab === '3',
            })}
            onClick={() => setActiveTab('3')}
          >
            <FormattedMessage id="Exterior" />
          </NavLinkPane>
        </NavItem>
        <NavItem>
          <NavLinkPane
            className={classNames({
              active: activeTab === '4',
            })}
            onClick={() => setActiveTab('4')}
          >
            <FormattedMessage id="Team" />
          </NavLinkPane>
        </NavItem>
        <NavItem>
          <NavLinkPane
            className={classNames({
              active: activeTab === '5',
            })}
            onClick={() => setActiveTab('5')}
          >
            <FormattedMessage id="My Images" />
          </NavLinkPane>
        </NavItem>
        <NavItem>
          <NavLinkPane
            className={classNames({
              active: activeTab === '6',
            })}
            onClick={() => setActiveTab('6')}
          >
            <FormattedMessage id="Customers" />
          </NavLinkPane>
        </NavItem>
      </Nav>
      <TabContent activeTab={activeTab} className="mt-1">
        <TabPane tabId="1" className="bg-transparent px-0">
          <div className="row mx-0 mb-2">
            <div className="col-md-4">
              <p>Logo:</p>
              <Show condition={locationImages?.PROFILE?.length}>
                <LogoImage src={locationImages?.PROFILE?.[0]?.googleUrl} />
              </Show>
            </div>
            <div className="col-md-8 pr-0">
              <div className="d-flex justify-content-end">
                <EditableRow
                  switchId="PROFILE"
                  onChange={switchId => handleSwitchChange(switchId)}
                  isChecked={editedFields?.PROFILE}
                />
              </div>
              <DropzoneImageInput
                ref={logoDropzoneRef}
                onChange={files => {
                  setLogoImage(prev => ({ ...(prev || {}), files }))
                }}
                disabled={!editedFields?.PROFILE || logoImage.submitting}
              />
              <p>
                <em>
                  <FormattedMessage id="The minimum is 250px by 250px (recommended 720px by 720px). Max size 5mb JPG or PNG." />
                </em>
              </p>

              <Show condition={!logoImage.updateSuccessfully}>
                <Button
                  color="primary"
                  disabled={
                    !editedFields?.PROFILE ||
                    !logoImage.files?.length ||
                    logoImage.submitting
                  }
                  onClick={() => handleSubmitImages(logoImage.files, 'PROFILE')}
                >
                  <Show condition={!logoImage.submitting}>
                    <FormattedMessage
                      id="Update image"
                      values={{ image: 'Logo' }}
                    />
                  </Show>
                  <Show condition={logoImage.submitting}>
                    <FormattedMessage id="Sending" />
                    ...
                    <Spinner size="sm" className="ml-25" />
                  </Show>
                </Button>
              </Show>
              <Show condition={logoImage.updateSuccessfully}>
                <p className="mb-0 success">
                  <Icon.CheckCircle size={16} className="mr-50" />
                  <FormattedMessage
                    id="The company image has been successfully updated!"
                    values={{ type: 'logo' }}
                  />
                </p>
              </Show>
            </div>
          </div>
          <div className="row mx-0">
            <div className="col-md-5">
              <p>
                <FormattedMessage id="Cover" />:
              </p>
              <Show condition={locationImages?.COVER?.length}>
                <LogoImage src={locationImages?.COVER?.[0]?.googleUrl} />
              </Show>
            </div>
            <div className="col-md-7 pr-0">
              <div className="d-flex justify-content-end">
                <EditableRow
                  switchId="COVER"
                  onChange={switchId => {
                    handleSwitchChange(switchId)
                    handleUpdateCoverOrLogoImage({
                      type: 'COVER',
                      updateSuccessfully: false,
                    })
                  }}
                  isChecked={editedFields?.COVER}
                />
              </div>
              <DropzoneImageInput
                ref={coverDropzoneRef}
                onChange={files => {
                  setCoverImage(prev => ({ ...(prev || {}), files }))
                }}
                disabled={!editedFields?.COVER || coverImage.submitting}
              />
              <p>
                <em>
                  Proporção recomendada de 16:9 (1280px por 720px ou 1920px por
                  1080px). Tamanho mínimo de 1Mb e tamanho máximo de 5Mb JPG ou
                  PNG.
                </em>
              </p>

              <Show condition={!coverImage.updateSuccessfully}>
                <Button
                  color="primary"
                  disabled={
                    !editedFields?.COVER ||
                    !coverImage.files?.length ||
                    coverImage.submitting
                  }
                  onClick={() => handleSubmitImages(coverImage.files, 'COVER')}
                >
                  <Show condition={!coverImage.submitting}>
                    <FormattedMessage
                      id="Update image"
                      values={{ image: 'Cover' }}
                    />
                  </Show>
                  <Show condition={coverImage.submitting}>
                    <FormattedMessage id="Sending" />
                    ...
                    <Spinner size="sm" className="ml-25" />
                  </Show>
                </Button>
              </Show>
              <Show condition={coverImage.updateSuccessfully}>
                <p className="mb-0 success">
                  <Icon.CheckCircle size={16} className="mr-50" />
                  <FormattedMessage
                    id="The company image has been successfully updated!"
                    values={{ type: 'cover' }}
                  />
                </p>
              </Show>
            </div>
          </div>
        </TabPane>
        <TabPane tabId="2" className="bg-transparent">
          <AdditionalImages
            id="INTERIOR"
            onSwitchChange={handleSwitchChange}
            editedFields={editedFields}
            hintText="O mínimo é 250px por 250px (recomendado 720px por 720px). Tamanho máximo de 5MB JPG ou PNG."
            images={locationImages?.INTERIOR}
            labelListPreview="Imagens do interior do local"
            sendButtonLabel="Atualizar imagens do interior"
            onSubmit={handleSubmitImages}
            onZoomImage={image => setZoomImage(image)}
            onExcludeImage={image => {
              setDeleteImage(image)
              setConfirmDeleteImage(true)
            }}
            excludingImage={excludingImage}
            successMessage="Imagens do interior atualizadas com sucesso!"
          />
        </TabPane>
        <TabPane tabId="3" className="bg-transparent">
          <AdditionalImages
            id="EXTERIOR"
            onSwitchChange={handleSwitchChange}
            editedFields={editedFields}
            hintText="O mínimo é 250px por 250px (recomendado 720px por 720px). Tamanho máximo de 5MB JPG ou PNG."
            images={locationImages?.EXTERIOR}
            labelListPreview="Imagens do exterior do local"
            sendButtonLabel="Atualizar imagens do exterior"
            onSubmit={handleSubmitImages}
            onZoomImage={image => setZoomImage(image)}
            onExcludeImage={image => {
              setDeleteImage(image)
              setConfirmDeleteImage(true)
            }}
            excludingImage={excludingImage}
            successMessage="Imagens do exterior atualizadas com sucesso!"
          />
        </TabPane>
        <TabPane tabId="4" className="bg-transparent">
          <AdditionalImages
            id="TEAMS"
            onSwitchChange={handleSwitchChange}
            editedFields={editedFields}
            hintText="O mínimo é 250px por 250px (recomendado 720px por 720px). Tamanho máximo de 5MB JPG ou PNG."
            images={locationImages?.TEAMS}
            labelListPreview="Imagens da equipe"
            sendButtonLabel="Atualizar imagens da equipe"
            onSubmit={handleSubmitImages}
            onZoomImage={image => setZoomImage(image)}
            onExcludeImage={image => {
              setDeleteImage(image)
              setConfirmDeleteImage(true)
            }}
            excludingImage={excludingImage}
            successMessage="Imagens da equipe atualizadas com sucesso!"
          />
        </TabPane>
        <TabPane tabId="5" className="bg-transparent">
          <AdditionalImages
            id="ADDITIONAL"
            onSwitchChange={handleSwitchChange}
            editedFields={editedFields}
            hintText="O mínimo é 250px por 250px (recomendado 720px por 720px). Tamanho máximo de 5MB JPG ou PNG."
            images={locationImages?.ADDITIONAL}
            labelListPreview="Imagens adicionais do local"
            sendButtonLabel="Atualizar imagens adicionais"
            onSubmit={handleSubmitImages}
            onZoomImage={image => setZoomImage(image)}
            onExcludeImage={image => {
              setDeleteImage(image)
              setConfirmDeleteImage(true)
            }}
            excludingImage={excludingImage}
            successMessage="Imagens adicionais atualizadas com sucesso!"
          />
        </TabPane>
        <TabPane tabId="6" className="bg-transparent">
          <AdditionalImages
            images={locationImages?.CUSTOMERS}
            onZoomImage={image => setZoomImage(image)}
            excludingImage={excludingImage}
            hideDropzone
          />
        </TabPane>
      </TabContent>
      <ZoomImageModal
        image={zoomImage}
        isOpen={!!zoomImage}
        toggle={() => setZoomImage(null)}
        excludingImage={excludingImage}
        onExcludeImage={image => {
          setDeleteImage(image)
          setConfirmDeleteImage(true)
        }}
      />
      <CallbackAlert
        open={confirmDeleteImage}
        setOpen={setConfirmDeleteImage}
        warnTitle="Are you sure?"
        warnMessage="Você deseja deletar a seguinte imagem?"
        successTitle="Deleted"
        successMessage="Image successfully deleted"
        errorTitle="Error"
        errorMessage="Failed to delete image"
        callback={() => handleDeleteImage(deleteImage)}
      />
    </>
  )
}

const NavLinkPane = styled(NavLink)`
  color: #626262 !important;

  &.active {
    font-weight: 600;
    color: #7367f0;
    &::after {
      background-color: red !important;
      transform: rotate(0deg) !important;
      left: 0px !important;
      width: 100% !important;
      bottom: 1px !important;
      top: auto !important;
    }
  }
`

const LogoImage = styled.img`
  max-width: 100%;
  object-fit: contain;
`

const AdditionalListPreview = styled.div`
  display: flex;
  align-items: center;
  height: 14rem;
  overflow-x: auto;
  max-width: 100%;
  flex-wrap: wrap;
`

const AdditionalImageCard = styled.div`
  position: relative;
  margin-right: 8px;

  &:hover {
    &::before {
      content: '';
      background: rgba(24, 24, 24, 0.8);
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
    }

    button {
      display: inline-block;
    }

    a {
      display: flex;
    }
  }

  img {
    object-fit: contain;
    max-height: 14rem;
    max-width: 14rem;
  }
`

AdditionalImageCard.MaximizeButton = styled(Button)`
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 1px;
  background-color: transparent !important;
`

AdditionalImageCard.ReportButton = styled.a`
  position: absolute;
  display: none;
  right: 5px;
  top: 5px;
  padding: 2px;
  border-color: #f4f5f6;
  background-color: white;
  color: #e42728;
  border-radius: 50%;

  &:hover {
    border-color: #f4f5f7;
    color: #e42728 !important;
    box-shadow: 0 8px 25px -8px #ea5455;
  }
`

AdditionalImageCard.ExcludeButton = styled(Button)`
  display: none;
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 2px;
`

const ZoomImage = styled.img`
  max-width: 100%;
  object-fit: contain;
`

const ModalHeaderWithButtons = styled(ModalHeader)`
  & > div {
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: center;
  }
`

const ReportButton = styled.a`
  padding: 5px 8px;
  border-color: #e42728 !important;
  background-color: #ea5455 !important;
  color: #fff;
  border-radius: 4px;

  &:hover {
    border-color: #e73d3e !important;
    color: #fff !important;
    box-shadow: 0 8px 25px -8px #ea5455;
  }
`

const ViewsCount = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  right: 10px;
  top: 10px;
  background-color: rgba(250, 250, 250, 0.8);
  border-radius: 4px;
  padding: 2px 5px;
`

export default EditImagesForm
